import React, { useState, useEffect, MouseEvent } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Assignment,
  CalendarToday,
  DoneAll,
} from '@mui/icons-material';
import { format, compareAsc } from 'date-fns';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';

// Import components
import DashboardStats from './DashboardStats';
import SearchControls from './SearchControls';
import ListView from './ListView';
import CalendarView from './CalendarView';
import CompletedView from './CompletedView';
import CompletionDialog from './CompletionDialog';
import ActionMenu from './ActionMenu';

// Import utilities
import { generateMockInspections } from './utils/mockData';

// Import types
import {
  Inspection,
  InspectionStatus,
  SortOption,
  Attachment,
  ChecklistItem,
  //   ApiResponse 
} from './types';
import transformInspectionData from './utils/transformData';
import InProgress from './InProgress';
import StartInspectionDialog from './StartInspectionDialog';

/**
 * InspectorDashboard - Responsive main dashboard for inspectors to manage their assigned inspections
 */
const InspectorDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // States
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [filteredInspections, setFilteredInspections] = useState<Inspection[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortOption, setSortOption] = useState<SortOption>('date-asc');
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [completionDialogOpen, setCompletionDialogOpen] = useState<boolean>(false);

  // Mobile-specific states
  // const [mobileDrawerOpen, setMobileDrawerOpen] = useState<boolean>(false);
  const [mobileTabsExpanded, setMobileTabsExpanded] = useState<boolean>(false);
  // eslint-disable-next-line no-console
  console.log(mobileTabsExpanded, isTablet, isSmallMobile)
  // Menu states
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<HTMLElement | null>(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [contextInspection, setContextInspection] = useState<Inspection | null>(null);

  // Stats counts
  const [todayCount, setTodayCount] = useState<number>(0);
  const [completedCount, setCompletedCount] = useState<number>(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [reportIssue, setReportIssue] = useState<boolean>(false);
  const [inspectionNotCompleted, setInspectionNotCompleted] = useState<boolean>(false);

  const [isInProgressVisible, setInProgressVisible] = useState(false);

  // Fetch inspections from API
  const fetchInspections = async (showLoading = true): Promise<void> => {
    if (showLoading) setLoading(true);

    try {
      const response = await httpRequest('GET', '/construction/application/inspection?Page=1&Size=10');

      if (response?.status === 'success') {
        const data = await transformInspectionData(response?.data?.Data)
        setInspections(data || []);
      } else {
        // Mock data for demo purposes
        const mockInspections = generateMockInspections();
        setInspections(mockInspections);
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
      toast.error('Failed to load inspections');

      // Mock data for demo purposes
      const mockInspections = generateMockInspections();
      setInspections(mockInspections);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const [showStartConfirmation, setShowStartConfirmation] = useState<boolean>(false);

  const [selectedInspectionForStart, setSelectedInspectionForStart] = useState<Inspection | null>(null);
  // Add this new handler function after handleUpdateStatus function (around line 250)
  const handleStartInspection = (inspection: Inspection): void => {
    setSelectedInspectionForStart(inspection);

    // Only show confirmation dialog if inspection is not already in progress
    if (inspection.status !== 'In Progress') {
      setShowStartConfirmation(true);
    } else {
      // If already in progress, go directly to InProgress page
      setInProgressVisible(true);
    }
  };

  // Add this new handler function after handleStartInspection
  const handleConfirmStartInspection = async (inspectionId: string): Promise<void> => {
    try {
      const payload = {
        InspectionID: Number(inspectionId),
        Status: "In Progress",
        Notes: "",
        Documents: []
      };

      const response = await httpRequest('PATCH', `/construction/application/inspection/update_status`, payload);

      if (response?.status === 'success') {
        toast.success('Inspection started successfully');

        // Close the confirmation dialog
        setShowStartConfirmation(false);

        // Refresh data to get updated inspections
        await fetchInspections(false);

        // Navigate to InProgress page
        setInProgressVisible(true);
      } else {
        throw new Error(response?.message || 'Failed to start inspection');
      }
    } catch (error) {
      console.error('Error starting inspection:', error);
      toast.error('Failed to start inspection');
    }
  };

  // Add this new handler function after handleConfirmStartInspection
  // const handleCloseInProgress = (): void => {
  //   setInProgressVisible(false);
  //   setSelectedInspectionForStart(null);
  // };

  const handleCloseStartConfirmation = (): void => {
    setShowStartConfirmation(false);
    setSelectedInspectionForStart(null);
  };

  const handleBackFromInProgress = (): void => {
    setInProgressVisible(false);
    setSelectedInspectionForStart(null);
  };

  const handleCompleteFromInProgress = async (
    completionType: 'pass' | 'fail' | 'not-completed',
    completionNotes: string,
    attachments: Attachment[],
    checklist: ChecklistItem[]
  ) => {
    if (!selectedInspectionForStart) return;
    setLoading(true);

    try {
      // Step 1: Upload attachments if any exist
      const uploadedDocuments: { Path: string }[] = [];
      const formData = new FormData();

      // Upload files one by one
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachment.file) {
            // Append the single file to form data
            formData.append('Files', attachment.file);
          }
        }

        // Call the upload API for files
        const uploadResponse = await httpRequest('POST',
          '/uploadPDF/document_Images?PermitName=Construction&FileType=Building_Inspection', formData,
          {
            'Content-Type': 'multipart/form-data',
            'x-upload-mode': 'multiple'
          }
        );

        if (uploadResponse?.status !== 'success') {
          throw new Error(`Failed to upload files: ${uploadResponse.statusText}`);
        }

        // Parse the response according to the actual format
        uploadResponse?.data?.files?.forEach(item => {
          uploadedDocuments.push({ Path: item.FilePath });
        });
      }

      // Step 2: Determine the status based on completion type
      let status = "Pass";
      if (completionType === 'fail') {
        status = "Fail";
      } else if (completionType === 'not-completed') {
        status = "Not Completed";
      }

      // Step 3: Update the inspection status with checklist data
      const updateData = {
        InspectionID: Number(selectedInspectionForStart?.inspectionId),
        Status: status,
        Notes: completionNotes,
        Documents: uploadedDocuments,
        InspectionCheckList: checklist // Include the checklist data
      };

      // Call the update status API
      const updateResponse = await httpRequest('PATCH',
        '/construction/application/inspection/update_status', updateData
      );

      if (updateResponse.status !== 'success') {
        throw new Error(`Failed to update inspection status: ${updateResponse.statusText}`);
      }

      // Show success message
      toast.success(`Inspection marked as ${status} successfully`);

      // Go back to dashboard
      setInProgressVisible(false);
      setSelectedInspectionForStart(null);

      // Refresh data to get updated inspections
      await fetchInspections();

    } catch (error) {
      console.error('Error in handleCompleteFromInProgress:', error);
      toast.error(`Failed to complete the inspection: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort inspections
  useEffect(() => {
    let filtered = [...inspections];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inspection => inspection.status === statusFilter);
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(inspection =>
        inspection.applicationNumber.toLowerCase().includes(query) ||
        inspection.applicationTitle.toLowerCase().includes(query) ||
        inspection.applicantName.toLowerCase().includes(query) ||
        inspection.propertyAddress.toLowerCase().includes(query)
      );
    }

    // Apply date filter from calendar if on calendar tab
    if (activeTab === 1 && selectedDate) {
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      filtered = filtered.filter(inspection => inspection.inspectionDate === selectedDateStr);
    }

    // Apply sorting
    switch (sortOption) {
      case 'date-asc':
        filtered.sort((a, b) => compareAsc(new Date(a.inspectionDate), new Date(b.inspectionDate)));
        break;
      case 'date-desc':
        filtered.sort((a, b) => compareAsc(new Date(b.inspectionDate), new Date(a.inspectionDate)));
        break;
      case 'priority-high':
        // eslint-disable-next-line no-case-declarations
        const priorityOrder = { 'Urgent': 1, 'High': 2, 'Normal': 3, 'Low': 4 } as const;
        filtered.sort((a, b) =>
          (priorityOrder[a.priority as keyof typeof priorityOrder] || 0) -
          (priorityOrder[b.priority as keyof typeof priorityOrder] || 0)
        );
        break;
      default:
        break;
    }

    setFilteredInspections(filtered);

    // Update counts
    setTotalCount(inspections.length);
    setCompletedCount(inspections.filter(item => item.status === 'Pass').length);
    setPendingCount(inspections.filter(item => item.status === 'Scheduled' || item.status === 'Rescheduled' || item.status === 'In Progress').length);

    const todayDateStr = format(new Date(), 'yyyy-MM-dd');
    setTodayCount(inspections.filter(item => item.inspectionDate === todayDateStr).length);

  }, [inspections, statusFilter, searchQuery, sortOption, activeTab, selectedDate]);

  // Initial data fetch
  useEffect(() => {
    fetchInspections();
  }, []);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setActiveTab(newValue);
    setMobileTabsExpanded(false); // Close mobile tabs when selection is made

    // Reset filters when switching tabs
    if (newValue === 0) {
      setStatusFilter('all');
    } else if (newValue === 1) {
      setSelectedDate(new Date());
    } else if (newValue === 2) {
      setStatusFilter('Pass');
    }
  };

  // Handle opening the completion dialog
  const handleOpenCompletionDialog = (inspection: Inspection, issue?: boolean, isNotCompleted?: boolean): void => {
    setReportIssue(issue)
    setInspectionNotCompleted(isNotCompleted)
    setSelectedInspection(inspection);
    setCompletionDialogOpen(true);
    setActionMenuAnchor(null);
  };

  // Handle closing the completion dialog
  const handleCloseCompletionDialog = (): void => {
    setCompletionDialogOpen(false);
    setSelectedInspection(null);
  };

  // Handle marking an inspection as complete with auto-refresh
  const handleMarkAsComplete = async (completionNotes: string, attachments: Attachment[]): Promise<void> => {
    if (!selectedInspection) return;
    setLoading(true);

    try {
      // Step 1: Upload attachments if any exist
      const uploadedDocuments: { Path: string }[] = [];
      const formData = new FormData();

      // Upload files one by one
      if (attachments.length > 0) {
        for (const attachment of attachments) {
          if (attachment.file) {
            // Append the single file to form data
            formData.append('Files', attachment.file);
          }
        }

        // Call the upload API for each file
        const uploadResponse = await httpRequest('POST',
          '/uploadPDF/document_Images?PermitName=Construction&FileType=Building_Inspection', formData,
          {
            'Content-Type': 'multipart/form-data',
            'x-upload-mode': 'multiple'
          }
        );

        if (uploadResponse?.status !== 'success') {
          throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
        }

        // Parse the response according to the actual format
        uploadResponse?.data?.files?.forEach(item => {
          uploadedDocuments.push({ Path: item.FilePath });
        });
      }

      // Step 2: Update the inspection status
      const status = reportIssue ? "Fail" : (inspectionNotCompleted ? "Not Completed" : "Pass");

      const updateData = {
        InspectionID: Number(selectedInspection?.inspectionId),
        Status: status,
        Notes: completionNotes,
        Documents: uploadedDocuments
      };

      // Call the update status API
      const updateResponse = await httpRequest('PATCH',
        '/construction/application/inspection/update_status', updateData
      );

      if (updateResponse.status !== 'success') {
        throw new Error(`Failed to update inspection status: ${updateResponse.statusText}`);
      }

      // Close the dialog first
      handleCloseCompletionDialog();

      // Show success message
      toast.success(`Inspection marked as ${status} successfully`);

      // Refresh data to get updated inspections
      await fetchInspections();

    } catch (error) {
      console.error('Error in handleMarkAsComplete:', error);
      toast.error(`Failed to complete the inspection: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle action menu
  const handleActionMenuOpen = (event: MouseEvent<HTMLElement>, inspection: Inspection): void => {
    setActionMenuAnchor(event.currentTarget);
    setContextInspection(inspection);
  };

  // Handle action menu close
  const handleActionMenuClose = (): void => {
    setActionMenuAnchor(null);
    setContextInspection(null);
  };

  // Handle inspection details view
  const handleViewDetails = (inspection: Inspection): void => {
    setSelectedInspection(inspection);
    handleActionMenuClose();
  };

  // Handle updating inspection status with auto-refresh
  const handleUpdateStatus = async (inspectionId: string, newStatus: InspectionStatus): Promise<void> => {
    try {
      const payload = {
        InspectionID: Number(inspectionId),
        Status: "InProgress",
        Notes: "",
        Documents: []
      }

      const response = await httpRequest('PATCH', `/construction/application/inspection/update_status`, payload);

      if (response?.status === 'success') {
        toast.success(`Inspection marked as ${newStatus}`);

        // Refresh data to get updated inspections
        await fetchInspections(false); // Don't show loading spinner for status updates
      } else {
        throw new Error(response?.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating inspection status:', error);
      toast.error(`Something went wrong while updating status`);
    }

    handleActionMenuClose();
  };

  return (
    <>
      {isInProgressVisible ?
        <InProgress
          inspection={selectedInspectionForStart}
          onBack={handleBackFromInProgress}
          onComplete={handleCompleteFromInProgress}
        />
        :
        <Container
          maxWidth="xl"
          sx={{
            mt: { xs: 3, md: 3 }, // Account for mobile app bar
            mb: { xs: 4, md: 8 },
            px: { xs: 2, sm: 3, md: 3 }
          }}
        >
          {/* Desktop Header */}
          {(
            <Box sx={{ mb: { xs: 3, md: 4 } }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                fontWeight="bold"
                sx={{
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' }
                }}
              >
                Inspector Dashboard
              </Typography>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
                sx={{
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  textAlign: 'center'
                }}
              >
                Manage your assigned inspections and track progress
              </Typography>
            </Box>
          )}

          {/* Dashboard Stats */}
          <Box sx={{
            mb: { xs: 3, md: 4 },
            '& > *': {
              minHeight: { xs: 'auto', md: 'inherit' }
            }
          }}>
            <DashboardStats
              todayCount={todayCount}
              pendingCount={pendingCount}
              completedCount={completedCount}
              totalCount={totalCount}
              inspections={inspections}
            />
          </Box>

          {/* Responsive Tabs */}
          <Box sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: { xs: 2, md: 3 },
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Mobile: Text-only tabs for clean display */}
            {isMobile ? (
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="inspection dashboard tabs"
                variant="fullWidth"
                sx={{
                  minHeight: 48,
                  '& .MuiTabs-flexContainer': {
                    width: '100%'
                  },
                  '& .MuiTabs-scroller': {
                    overflow: 'hidden !important'
                  },
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    px: 1,
                    minWidth: 0,
                    maxWidth: '100%',
                    flex: 1,
                    textTransform: 'none'
                  }
                }}
              >
                <Tab label="All" />
                <Tab label="Calendar" />
                <Tab label="Completed" />
              </Tabs>
            ) : (
              /* Desktop: Full tabs with icons */
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="inspection dashboard tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    px: 2,
                    minWidth: 160,
                    textTransform: 'none'
                  }
                }}
              >
                <Tab
                  label="All Inspections"
                  icon={<Assignment />}
                  iconPosition="start"
                />
                <Tab
                  label="Calendar View"
                  icon={<CalendarToday />}
                  iconPosition="start"
                />
                <Tab
                  label="Completed"
                  icon={<DoneAll />}
                  iconPosition="start"
                />
              </Tabs>
            )}
          </Box>

          {/* Search and Filters */}
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <SearchControls
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filterMenuAnchor={filterMenuAnchor}
              setFilterMenuAnchor={setFilterMenuAnchor}
              sortMenuAnchor={sortMenuAnchor}
              setSortMenuAnchor={setSortMenuAnchor}
              setStatusFilter={setStatusFilter}
              setSortOption={setSortOption}
            />
          </Box>

          {/* Tab Content */}
          <Box sx={{
            mt: { xs: 2, md: 3 },
            '& .MuiCard-root': {
              mb: { xs: 1.5, md: 2 }
            }
          }}>
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {activeTab === 0 && (
              <ListView
                filteredInspections={filteredInspections}
                handleOpenCompletionDialog={handleOpenCompletionDialog}
                handleViewDetails={handleViewDetails}
                handleActionMenuOpen={handleActionMenuOpen}
                selectedInspection={selectedInspection}
                setSelectedInspection={setSelectedInspection}
                handleUpdateStatus={handleUpdateStatus}
                handleStartInspection={handleStartInspection}
              />
            )}

            {activeTab === 1 && (
              <CalendarView
                filteredInspections={filteredInspections}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                handleOpenCompletionDialog={handleOpenCompletionDialog}
                handleViewDetails={handleViewDetails}
                handleActionMenuOpen={handleActionMenuOpen}
                selectedInspection={selectedInspection}
                setSelectedInspection={setSelectedInspection}
                handleUpdateStatus={handleUpdateStatus}
                inspections={inspections}
                handleStartInspection={handleStartInspection}
              />
            )}

            {activeTab === 2 && (
              <CompletedView
                filteredInspections={filteredInspections}
                handleViewDetails={handleViewDetails}
                handleActionMenuOpen={handleActionMenuOpen}
                selectedInspection={selectedInspection}
                setSelectedInspection={setSelectedInspection}
              />
            )}
          </Box>

          {/* Completion Dialog */}
          <CompletionDialog
            open={completionDialogOpen}
            onClose={handleCloseCompletionDialog}
            inspection={selectedInspection}
            onComplete={handleMarkAsComplete}
            loading={loading}
            reportIssue={reportIssue}
            inspectionNotCompleted={inspectionNotCompleted}
          />

          {/* Action Menu */}
          <ActionMenu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={handleActionMenuClose}
            inspection={contextInspection}
            onViewDetails={handleViewDetails}
            onComplete={handleOpenCompletionDialog}
            onUpdateStatus={handleUpdateStatus}
          />
          <StartInspectionDialog
            open={showStartConfirmation}
            inspection={selectedInspectionForStart}
            onClose={handleCloseStartConfirmation}
            onConfirmStart={handleConfirmStartInspection}
            loading={loading}
          />
        </Container>}
    </>
  );
};

export default InspectorDashboard;